import React, { useMemo, useState } from "react";
import { Alert, Badge, Card, Table } from "react-bootstrap";
import styled from "styled-components";
import { FormattedMessage, useIntl } from "react-intl";
import Selector from "../../../../components/selector";
import {
  DateRange,
  MetricRange,
  SegmentMetricsEntity,
  ScenarioMetricsEntity,
} from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { useExperimentResults } from "../application/hooks/useExperimentResults";
import { messages } from "../experiment.messages";

/** Stable id so the dimension caption can be a real <label htmlFor>. */
const DIMENSION_SELECT_ID = "experiment-segments-dimension";

export interface SegmentsTableProps {
  propertyTicker: string;
  experimentId: string;
  // Read-only props from ExperimentResultsPage, which owns this shared state.
  range: MetricRange;
  custom: DateRange | undefined;
  repository: ExperimentRepository;
}

/**
 * Labels for the dimensions the backend sends today. An unknown dimension still renders — with its
 * raw key as the label — rather than being dropped, so adding a third breakdown server-side needs
 * no front-end release to become visible.
 */
const DIMENSION_LABELS: Record<string, keyof typeof messages.results.segments> = {
  device: "dimensionDevice",
  origin_class: "dimensionOriginClass",
};

const DimmedRow = styled.tr<{ $dimmed: boolean }>`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
`;

function formatPercent(value: number, intl: ReturnType<typeof useIntl>): string {
  return `${intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function conversionPair(
  a: ScenarioMetricsEntity,
  b: ScenarioMetricsEntity,
  intl: ReturnType<typeof useIntl>
): string {
  return `${formatPercent(a.conversionRate, intl)} / ${formatPercent(b.conversionRate, intl)}`;
}

/**
 * Per-device / per-origin breakdown of the experiment. Every figure — including each segment's own
 * confidence and lift — is computed by `backoffice-manager`; this section only chooses which rows
 * to show and how to format them.
 *
 * Self-contained data-fetching section like its siblings, sharing the shell's query through React
 * Query's identical-key de-duplication.
 */
export function SegmentsTable({
  propertyTicker,
  experimentId,
  range,
  custom,
  repository,
}: SegmentsTableProps) {
  const intl = useIntl();
  const { data, isLoading, isError } = useExperimentResults(
    propertyTicker,
    experimentId,
    range,
    repository,
    custom
  );

  const segments: SegmentMetricsEntity[] = useMemo(() => data?.segments ?? [], [data?.segments]);

  // Built from the response rather than from a hardcoded list, so the selector always offers
  // exactly the breakdowns this experiment actually has.
  const dimensions = useMemo(
    () => Array.from(new Set(segments.map(segment => segment.dimension))),
    [segments]
  );

  const [selectedDimension, setSelectedDimension] = useState<string | null>(null);
  // `selectedDimension` stays null until the user picks one, so the first available dimension is
  // shown by default and a response that later loses that dimension falls back gracefully.
  const activeDimension =
    selectedDimension && dimensions.includes(selectedDimension) ? selectedDimension : dimensions[0];

  const rows = segments.filter(segment => segment.dimension === activeDimension);

  const dimensionOptions = dimensions.map(dimension => {
    const labelKey = DIMENSION_LABELS[dimension];
    return {
      value: dimension,
      label: labelKey ? intl.formatMessage(messages.results.segments[labelKey] as any) : dimension,
    };
  });
  const selectedOption = dimensionOptions.find(option => option.value === activeDimension) ?? null;

  const title = (
    <h6 className="mb-3">
      <FormattedMessage {...messages.results.segments.title} />
    </h6>
  );

  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          {title}
          <div className="text-muted" data-testid="segments-loading">
            <FormattedMessage {...messages.results.loading} />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <Card.Body>
          {title}
          <Alert variant="danger" className="mb-0" data-testid="segments-error">
            <FormattedMessage {...messages.results.dataUnavailable} />
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <Card.Body>
          {title}
          <p className="text-muted mb-0" data-testid="segments-empty">
            <FormattedMessage {...messages.results.segments.empty} />
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        {title}

        {dimensionOptions.length > 1 && (
          <div style={{ minWidth: 220, maxWidth: 320 }} className="mb-3">
            {/* A real <label> bound to react-select's own input: a plain div above the dropdown
                left it unnamed for a screen reader. */}
            <label htmlFor={DIMENSION_SELECT_ID} className="text-muted small mb-1 d-block">
              <FormattedMessage {...messages.results.segments.dimensionSelector} />
            </label>
            <Selector
              inputId={DIMENSION_SELECT_ID}
              isClearable={false}
              options={dimensionOptions}
              value={selectedOption}
              onChange={(option: { value: string } | null) =>
                option && setSelectedDimension(option.value)
              }
            />
          </div>
        )}

        <Table hover responsive className="mb-0">
          <thead>
            <tr>
              <th>
                <FormattedMessage {...messages.results.segments.columnSegment} />
              </th>
              <th>
                <FormattedMessage {...messages.results.segments.columnVisitors} />
              </th>
              <th>
                <FormattedMessage {...messages.results.segments.columnConversion} />
              </th>
              <th>
                <FormattedMessage {...messages.results.segments.columnLift} />
              </th>
              <th>
                <FormattedMessage {...messages.results.segments.columnConfidence} />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(segment => (
              // Dimming is driven by the backend's own `smallSample` flag (< 500 combined valid
              // visitors), never by a threshold re-implemented here.
              <DimmedRow
                key={`${segment.dimension}-${segment.value}`}
                $dimmed={segment.smallSample}
                data-testid={`segment-row-${segment.value}`}>
                <td>
                  {segment.value}{" "}
                  {segment.smallSample && (
                    <Badge variant="secondary" data-testid={`segment-small-${segment.value}`}>
                      <FormattedMessage {...messages.results.segments.smallSample} />
                    </Badge>
                  )}
                </td>
                <td>
                  {intl.formatNumber(segment.scenarioA.validVisitors)} /{" "}
                  {intl.formatNumber(segment.scenarioB.validVisitors)}
                </td>
                <td>{conversionPair(segment.scenarioA, segment.scenarioB, intl)}</td>
                <td>
                  {intl.formatNumber(segment.liftPct, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                  %
                </td>
                <td>
                  {/* Segment confidence arrives as a FRACTION (like the response-level one, unlike
                      the list item's 0..100) — converted here, at the point of display. */}
                  {intl.formatNumber(segment.confidence * 100, { maximumFractionDigits: 0 })}%
                </td>
              </DimmedRow>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}
